/**
 * PATH: apps/qa/src/lib/api/participants.ts
 * PURPOSE: API module for per-job QA role grants (/qa/participants/).
 * WHY: JobParticipant grants were Django-Admin-only — a QA lead had no way to
 *      put a worker on a job from the product, so non-admin workers hit 403s
 *      on every evidence/checklist write. Pairs with the BE self-service API
 *      shipped in quotech-backend (qa/api/views/participants.py).
 */

import { api, normaliseList } from './helpers'
import type { JobParticipant, RoleOnJob } from '@/types/qa'

export interface AddParticipantPayload {
  job: number
  user_email: string
  role_on_job: RoleOnJob
  can_sign_hold_points?: boolean
}

export interface UpdateParticipantPayload {
  role_on_job?: RoleOnJob
  can_sign_hold_points?: boolean
}

export const participantApi = {
  getParticipants: async (params: { job_id: number | string }): Promise<{ results: JobParticipant[]; count: number }> => {
    const response = await api().get('/qa/participants/', { params })
    return normaliseList(response.data)
  },

  /** Grant (or re-grant — the backend upserts the role) QA access on a job. */
  addParticipant: async (payload: AddParticipantPayload): Promise<JobParticipant> => {
    const response = await api().post('/qa/participants/', payload)
    return response.data
  },

  updateParticipant: async (id: string, payload: UpdateParticipantPayload): Promise<JobParticipant> => {
    const response = await api().patch(`/qa/participants/${id}/`, payload)
    return response.data
  },

  removeParticipant: async (id: string): Promise<void> => {
    await api().delete(`/qa/participants/${id}/`)
  },
}
